import { ProtectedComponent } from '@splunk/olly-common/RBAC';
import type { ProtectedComponentProps, Endpoint } from '@splunk/olly-common/RBAC';
import {
    userHasAccessToAllEndpoints,
    userHasAccessToAnyEndpoint,
} from '@splunk/olly-common/RBAC/capabilityCheckUtils';
import { CapabilityValues } from '@splunk/olly-services/lib/services/CurrentUser/Capabilities';
import { useCurrentUserStore } from '@splunk/olly-services/lib/services/CurrentUser/CurrentUserStoreProvider';

import React, { useEffect, useState } from 'react';

type ProtectedComponentWrapperType = Omit<ProtectedComponentProps, 'userCapabilities'>;

type useUserCapabilitiesUtilsType = {
    capabilities: CapabilityValues[];
    hasPermissionForAllEndpoints: (endpoints: Endpoint[]) => boolean;
    hasPermissionForAnyEndpoint: (endpoints: Endpoint[][]) => boolean;
};

/**
 * Custom hook to manage and evaluate user capabilities.
 *
 * This hook provides access to the current user's capabilities.
 *
 * @returns {useUserCapabilitiesUtilsType} An object containing:
 * - `capabilities`: An array of the user's current capability values.
 *
 */
export const useUserCapabilitiesUtils = (): useUserCapabilitiesUtilsType => {
    const currentUser = useCurrentUserStore();
    const [capabilities, setCapabilities] = useState<CapabilityValues[]>([]);

    useEffect(() => {
        currentUser.userCapabilities().then(setCapabilities);
    }, [currentUser]);

    const hasPermissionForAllEndpoints = (endpoints: Endpoint[]): boolean =>
        userHasAccessToAllEndpoints(endpoints, capabilities);

    const hasPermissionForAnyEndpoint = (listOfEndpoints: Endpoint[][]): boolean =>
        userHasAccessToAnyEndpoint(listOfEndpoints, capabilities);

    return { capabilities, hasPermissionForAllEndpoints, hasPermissionForAnyEndpoint };
};

/**
 * A wrapper component that simplifies the usage of the `ProtectedComponent` by automatically
 * injecting user capabilities. This eliminates the need for all references to explicitly pass
 * the `userCapabilities` prop, ensuring a cleaner and more consistent implementation.
 *
 * @param props - The properties required by the `ProtectedComponentWrapper` and subsequently passed to `ProtectedComponent`.
 * @returns A `ProtectedComponent` with user capabilities automatically provided.
 */
export const ProtectedComponentWrapper: React.FC<ProtectedComponentWrapperType> = (
    props: ProtectedComponentWrapperType
) => {
    const { capabilities } = useUserCapabilitiesUtils();
    return <ProtectedComponent userCapabilities={capabilities} {...props} />;
};
